import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SurveyStorageService } from './survey-storage.service';
import { GraphQLSurveyService } from './graphql-survey.service';

export interface Survey {
  id: string;
  name: string;
  description?: string;
  questions?: Question[];
}

export interface Question {
  id: string;
  surveyId: string;
  prompt: string;
  type: string;
  options?: string[];
  required: boolean;
  order: number;
}

export interface SurveyResponse {
  id: string;
  surveyId: string;
  questionId: string;
  answer: string;
  notes?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SurveyService {
  private currentSurveySubject = new BehaviorSubject<Survey | null>(null);
  private currentQuestionSubject = new BehaviorSubject<Question | null>(null);
  private showSurveySubject = new BehaviorSubject<boolean>(false);
  private questionsSubject = new BehaviorSubject<Question[]>([]);
  private currentQuestionIndexSubject = new BehaviorSubject<number>(0);

  public currentSurvey$ = this.currentSurveySubject.asObservable();
  public currentQuestion$ = this.currentQuestionSubject.asObservable();
  public showSurvey$ = this.showSurveySubject.asObservable();
  public questions$ = this.questionsSubject.asObservable();
  public currentQuestionIndex$ = this.currentQuestionIndexSubject.asObservable();

  private configuredDismissHours = 0; // Default 24 hours
  private isInitializing = false;

  constructor(
    private graphqlSurveyService: GraphQLSurveyService,
    private surveyStorageService: SurveyStorageService
  ) {}

  /**
   * Configure dismiss hours for surveys
   */
  setDismissHours(hours: number): void {
    this.configuredDismissHours = hours;
  }

  /**
   * Initialize survey system - check for available surveys
   * The GraphQL backend handles all targeting and dismissal filtering
   */
  async initializeSurveys(userId: string): Promise<void> {
    // Prevent concurrent initialization
    if (this.isInitializing) {
      return;
    }

    this.isInitializing = true;

    try {
      // Clean up expired local dismissals
      await this.surveyStorageService.cleanupExpiredDismissals();

      // Get active surveys from GraphQL (already filtered by targeting and dismissals)
      const activeSurveys = await this.graphqlSurveyService.fetchActiveSurveys();

      if (activeSurveys.length === 0) {
        this.showSurveySubject.next(false);
        return;
      }

      // Find the first survey the user hasn't fully responded to
      for (const graphqlSurvey of activeSurveys) {
        // Check local dismissal (belt and suspenders with server-side check)
        const isDismissed = await this.surveyStorageService.isSurveyDismissed(graphqlSurvey.id, userId);
        if (isDismissed) {
          continue;
        }

        // Convert GraphQL questions to local format
        const questions: Question[] = (graphqlSurvey.questions || []).map(q => ({
          id: q.id,
          surveyId: q.surveyId,
          prompt: q.prompt,
          type: q.type,
          options: q.options ?? undefined,
          required: q.required,
          order: q.order
        }));

        if (questions.length === 0) {
          continue;
        }

        // Get user's responses for this survey
        const userResponses = await this.graphqlSurveyService.fetchUserResponsesForSurvey(graphqlSurvey.id);
        const userAnsweredQuestionIds = userResponses.map(r => r.questionId).filter(Boolean);

        // Find the first unanswered question (for resume support)
        let startIndex = 0;
        for (let i = 0; i < questions.length; i++) {
          if (!userAnsweredQuestionIds.includes(questions[i].id)) {
            startIndex = i;
            break;
          }
          // If all questions answered, skip this survey
          if (i === questions.length - 1) {
            startIndex = -1; // Mark as fully completed
          }
        }

        // Skip if user has answered all questions
        if (startIndex === -1) {
          continue;
        }

        // Convert to local survey format
        const survey: Survey = {
          id: graphqlSurvey.id,
          name: graphqlSurvey.name,
          description: graphqlSurvey.description ?? undefined,
          questions
        };

        // Set current survey and questions state
        this.currentSurveySubject.next(survey);
        this.questionsSubject.next(questions);
        this.currentQuestionIndexSubject.next(startIndex);
        this.currentQuestionSubject.next(questions[startIndex]);
        this.showSurveySubject.next(true);

        return;
      }

      this.showSurveySubject.next(false);

    } catch (error) {
      console.error('Error initializing surveys:', error);
      this.showSurveySubject.next(false);
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Submit survey response
   * @param _userId - Not used (auth context provides user ID to GraphQL backend)
   * @param answer - The answer to submit
   */
  public async submitResponse(_userId: string, answer: string): Promise<SurveyResponse | null> {
    try {
      const currentSurvey = this.currentSurveySubject.value;
      const currentQuestion = this.currentQuestionSubject.value;

      if (!currentSurvey || !currentQuestion) {
        throw new Error('No current survey or question');
      }

      // Determine if answer should be stored as numeric value
      let numericValue: number | undefined;
      if (currentQuestion.type === 'RATING' || currentQuestion.type === 'NUMBER') {
        const parsed = parseInt(answer, 10);
        if (!isNaN(parsed)) {
          numericValue = parsed;
        }
      }

      await this.graphqlSurveyService.createSurveyResponse(
        currentSurvey.id,
        currentQuestion.id,
        answer,
        numericValue
      );

      const response: SurveyResponse = {
        id: `${currentSurvey.id}-${currentQuestion.id}`, // Pseudo-ID since GraphQL returns success enum
        surveyId: currentSurvey.id,
        questionId: currentQuestion.id,
        answer: answer
      };

      return response;

    } catch (error) {
      console.error('Error submitting survey response:', error);
      return null;
    }
  }

  /**
   * Update response with additional notes
   * Note: Currently not supported via GraphQL - feedback is logged but not persisted
   */
  public async updateResponseNotes(_responseId: string, notes: string): Promise<boolean> {
    // TODO: Add updateSurveyResponse mutation to persist notes
    console.log('Additional feedback received:', notes);
    return true;
  }

  /**
   * Dismiss current survey
   */
  public async dismissCurrentSurvey(userId: string): Promise<void> {
    try {
      const currentSurvey = this.currentSurveySubject.value;

      if (!currentSurvey) {
        return;
      }

      // Track dismissal on server via GraphQL
      try {
        await this.graphqlSurveyService.dismissSurvey(currentSurvey.id);
        console.log('Dismissal tracked via GraphQL');
      } catch (graphqlError) {
        console.error('Error tracking dismissal via GraphQL:', graphqlError);
        // Continue with local dismissal even if GraphQL fails
      }

      // Also track dismissal locally for immediate effect
      await this.surveyStorageService.dismissSurvey(
        currentSurvey.id,
        userId,
        this.configuredDismissHours
      );

      this.hideSurvey();
      console.log('Survey dismissed successfully');
    } catch (error) {
      console.error('Error dismissing survey:', error);
    }
  }

  /**
   * Track that a survey was viewed (shown to user)
   * This is called when the survey popup is displayed
   */
  public async trackSurveyView(surveyId: string): Promise<void> {
    try {
      await this.graphqlSurveyService.viewSurvey(surveyId);
      console.log('Survey view tracked');
    } catch (error) {
      // Don't let view tracking failure affect the user experience
      console.error('Error tracking survey view:', error);
    }
  }

  /**
   * Hide current survey (after completion or dismissal)
   */
  public hideSurvey(): void {
    this.currentSurveySubject.next(null);
    this.currentQuestionSubject.next(null);
    this.showSurveySubject.next(false);
    this.questionsSubject.next([]);
    this.currentQuestionIndexSubject.next(0);
  }

  /**
   * Advance to the next question
   * @returns true if advanced to next question, false if no more questions
   */
  public advanceToNextQuestion(): boolean {
    const questions = this.questionsSubject.value;
    const currentIndex = this.currentQuestionIndexSubject.value;

    if (currentIndex < questions.length - 1) {
      const nextIndex = currentIndex + 1;
      this.currentQuestionIndexSubject.next(nextIndex);
      this.currentQuestionSubject.next(questions[nextIndex]);
      return true;
    }

    return false;
  }

  /**
   * Check if currently on the last question
   */
  public isLastQuestion(): boolean {
    const questions = this.questionsSubject.value;
    const currentIndex = this.currentQuestionIndexSubject.value;
    return currentIndex >= questions.length - 1;
  }

  /**
   * Get total number of questions in current survey
   */
  public getTotalQuestions(): number {
    return this.questionsSubject.value.length;
  }

  /**
   * Get current question number (1-based for display)
   */
  public getCurrentQuestionNumber(): number {
    return this.currentQuestionIndexSubject.value + 1;
  }

  /**
   * Get parsed options for multiple choice, rating, and thumbs questions
   */
  public getParsedOptions(question: Question): string[] {
    const questionType = question.type?.toUpperCase();

    // For THUMBS questions, provide default options if none specified
    if (questionType === 'THUMBS') {
      if (question.options && question.options.length > 0) {
        return question.options;
      }
      return ['👍 Yes', '👎 No'];
    }

    // Options are now directly available as an array from GraphQL
    if (!question.options || question.options.length === 0) {
      return [];
    }

    // Check for Multiple Choice or Rating types
    if (questionType !== 'MULTIPLE_CHOICE' && questionType !== 'RATING') {
      return [];
    }

    return question.options;
  }

}